/**
 * @module engine3d/snap
 * @summary Turns a mouse ray into something worth connecting to: a nozzle, a
 *   point on an existing run, or a free point on the working plane.
 * @description
 *   Drawing pipe in 3D lives or dies on this. A click has to land on the
 *   nozzle you aimed at, not near it, and it has to do so at any zoom — so the
 *   tolerance is measured in SCREEN PIXELS and converted to world units at the
 *   candidate's own depth. A tolerance in world units would be unusably tight
 *   zoomed out and unusably loose zoomed in.
 *
 *   Candidates are ranked by kind before distance: a nozzle beats a pipe, and
 *   a pipe beats empty space. Aiming at a flange that happens to sit in front
 *   of a passing run should join the flange.
 */
import type { PortKey, Shape } from '../types';
import { S } from '../engine/state';
import { rayAxisPoint, rayPlanePoint, v3, vAdd, vDot, vMul, vSub, type Vec3 } from './math3';
import { Camera } from './camera';
import { portWorld, routePipe3 } from './pipes';
import { centreLine, freeEnd, type AxisMode } from './pipes3';

/** How close to a target a click has to be, in screen pixels. */
const TOL_PX = 15;
/** Ports of equipment further than this from the ray are not even considered. */
const MAX_DIST = 1e7;

export type SnapKind = 'port' | 'end' | 'pipe' | 'plane';

export interface SnapResult {
  kind: SnapKind;
  /** World position of the snap. */
  p: Vec3;
  /** Outward direction, for a nozzle. */
  n?: Vec3;
  shapeId?: string;
  port?: PortKey;
  /** Id of the run teed into — a 3D pipe id or a 2D connector id. */
  pipeId?: string;
  /** Position along that run, 0..1. */
  t?: number;
  /**
   * Set when the snap landed on a run's LOOSE END rather than part-way along
   * it. That is the difference between continuing a run and branching off it,
   * and only the caller drawing pipe cares which.
   */
  endOf?: { pipeId: string; which: 'from' | 'to' };
  /** Distance along the ray, for ranking. */
  dist: number;
}

/** World units per screen pixel at distance `t` along the view ray. */
function worldPerPixel(cam: Camera, viewportH: number, t: number): number {
  const f = cam.scale(viewportH);
  return cam.kind === 'perspective' ? Math.max(1e-4, t) / f : cam.distance / f;
}

/** Closest approach between a ray and a point: distance along, and offset. */
function rayPoint(o: Vec3, d: Vec3, q: Vec3): { t: number; off: number } {
  const t = vDot(vSub(q, o), d);
  if (t < 0) return { t: 0, off: MAX_DIST };
  const c = vAdd(o, vMul(d, t));
  return { t, off: Math.hypot(c.x - q.x, c.y - q.y, c.z - q.z) };
}

/** Closest approach between a ray and a segment. */
function raySegment(o: Vec3, d: Vec3, a: Vec3, b: Vec3): { t: number; off: number; f: number } {
  const ab = vSub(b, a);
  const len2 = vDot(ab, ab);
  if (len2 < 1e-9) {
    const r = rayPoint(o, d, a);
    return { t: r.t, off: r.off, f: 0 };
  }
  // Standard closest-points-between-two-lines solve, clamped to the segment.
  const w0 = vSub(o, a);
  const A = 1;
  const B = vDot(d, ab);
  const C = len2;
  const D = vDot(d, w0);
  const E = vDot(ab, w0);
  const den = A * C - B * B;
  let f = Math.abs(den) < 1e-9 ? 0 : (A * E - B * D) / den;
  f = Math.max(0, Math.min(1, f));
  const q = vAdd(a, vMul(ab, f));
  const r = rayPoint(o, d, q);
  return { t: r.t, off: r.off, f };
}

/** The 3D centre-line of a 2D connector, for teeing onto it. */
function connLine(id: string): Vec3[] | null {
  const cn = S.connections.find((c) => c.id === id);
  if (!cn || cn.hide3) return null;
  const f = S.shapes.find((s) => s.id === cn.fromId);
  const t = S.shapes.find((s) => s.id === cn.toId);
  if (!f || !t) return null;
  return routePipe3(portWorld(f, cn.fromPort || 'E'), portWorld(t, cn.toPort || 'W'), cn).path;
}

export interface SnapOptions {
  /** Height of the working plane used when nothing else is hit. */
  planeY: number;
  /** Grid step for a free point. 0 disables snapping. */
  grid: number;
  /** Ignore this run when teeing — a pipe cannot branch off itself. */
  excludePipe?: string;
  /** Skip nozzle snapping (used while dragging a mid-run vertex). */
  noPorts?: boolean;
}

/**
 * Resolve a mouse ray to the best thing to connect to.
 * Always returns something: with nothing else in range, a point on the working
 * plane, which is what lets a run be drawn out into open space.
 */
export function snapRay(
  o: Vec3,
  d: Vec3,
  cam: Camera,
  viewportH: number,
  opts: SnapOptions,
): SnapResult {
  let best: SnapResult | null = null;
  const consider = (cand: SnapResult, off: number) => {
    const tol = TOL_PX * worldPerPixel(cam, viewportH, cand.dist);
    if (off > tol) return;
    // Kind first, then proximity: a nozzle in range always wins over a pipe,
    // and a loose end beats the run it belongs to. Clicking the very tip of a
    // run means the tip — carrying on from there, not branching off it a
    // hair's breadth from where it stops.
    const rank = (k: SnapKind) => (k === 'port' ? 0 : k === 'end' ? 1 : k === 'pipe' ? 2 : 3);
    if (!best || rank(cand.kind) < rank(best.kind) || (rank(cand.kind) === rank(best.kind) && cand.dist < best.dist))
      best = cand;
  };

  // ── Nozzles ──
  if (!opts.noPorts) {
    const keys: PortKey[] = ['N', 'S', 'E', 'W'];
    for (const s of S.shapes as Shape[]) {
      if (s.type === 'zonebox' || s.type === 'textlabel' || s.type === 'processarrow') continue;
      const seen: Vec3[] = [];
      for (const k of keys) {
        const wp = portWorld(s, k);
        // Elbows alias two cardinals onto each real face; offer each face once.
        if (seen.some((q) => Math.hypot(q.x - wp.p.x, q.y - wp.p.y, q.z - wp.p.z) < 0.5)) continue;
        seen.push(wp.p);
        const r = rayPoint(o, d, wp.p);
        consider({ kind: 'port', p: wp.p, n: wp.n, shapeId: s.id, port: k, dist: r.t }, r.off);
      }
    }
  }

  // ── Loose ends, which are continued rather than branched off ──
  for (const q of S.pipes3) {
    if (q.id === opts.excludePipe) continue;
    for (const which of ['from', 'to'] as const) {
      const e = freeEnd(q, which);
      if (!e) continue;
      const r = rayPoint(o, d, e);
      consider(
        {
          kind: 'end',
          p: e,
          pipeId: q.id,
          t: which === 'from' ? 0 : 1,
          endOf: { pipeId: q.id, which },
          dist: r.t,
        },
        r.off,
      );
    }
  }

  // ── Existing runs, 3D-drawn and 2D-derived alike ──
  const lines: { id: string; pts: Vec3[] }[] = [];
  for (const q of S.pipes3) {
    if (q.id === opts.excludePipe) continue;
    lines.push({ id: q.id, pts: centreLine(q) });
  }
  for (const cn of S.connections) {
    const l = connLine(cn.id);
    if (l) lines.push({ id: cn.id, pts: l });
  }
  for (const line of lines) {
    if (line.pts.length < 2) continue;
    // Cumulative length, so `t` is a real fraction of the run rather than of
    // its vertex count — a tee has to stay put when a neighbouring leg moves.
    const segLen: number[] = [];
    let total = 0;
    for (let i = 1; i < line.pts.length; i++) {
      const L = Math.hypot(
        line.pts[i].x - line.pts[i - 1].x,
        line.pts[i].y - line.pts[i - 1].y,
        line.pts[i].z - line.pts[i - 1].z,
      );
      segLen.push(L);
      total += L;
    }
    if (total < 1e-6) continue;
    let before = 0;
    for (let i = 0; i < segLen.length; i++) {
      const r = raySegment(o, d, line.pts[i], line.pts[i + 1]);
      const at = vAdd(line.pts[i], vMul(vSub(line.pts[i + 1], line.pts[i]), r.f));
      consider(
        { kind: 'pipe', p: at, pipeId: line.id, t: (before + segLen[i] * r.f) / total, dist: r.t },
        r.off,
      );
      before += segLen[i];
    }
  }

  if (best) return best;

  // ── Working plane ──
  const denom = d.y;
  let p: Vec3;
  let dist = 0;
  if (Math.abs(denom) > 1e-5) {
    const t = (opts.planeY - o.y) / denom;
    if (t > 0) {
      p = vAdd(o, vMul(d, t));
      dist = t;
    } else {
      // Looking away from the plane — put the point a sensible way down the ray
      // rather than refusing the click.
      dist = cam.distance;
      p = vAdd(o, vMul(d, dist));
      p.y = opts.planeY;
    }
  } else {
    dist = cam.distance;
    p = vAdd(o, vMul(d, dist));
    p.y = opts.planeY;
  }
  if (opts.grid > 0) {
    p = v3(Math.round(p.x / opts.grid) * opts.grid, p.y, Math.round(p.z / opts.grid) * opts.grid);
  }
  return { kind: 'plane', p, dist };
}


// ─── Placing a free point ────────────────────────────────────────────────

/**
 * Decide which world axis the cursor is being dragged along, and return the
 * exact point on it.
 *
 * The obvious implementation — intersect the ray with a horizontal plane and
 * then keep the dominant axis — cannot draw a vertical leg AT ALL, because
 * every point on a horizontal plane has the same height as the one you started
 * from. So the axis is chosen in SCREEN space instead: each candidate axis is
 * intersected with the ray, projected back to pixels, and the one that lands
 * nearest the cursor wins. The result is then exactly on that axis, which is
 * what makes a riser genuinely vertical rather than nearly so.
 */
export function axisSnap(
  last: Vec3,
  o: Vec3,
  d: Vec3,
  cam: Camera,
  viewportW: number,
  viewportH: number,
  px: number,
  py: number,
  mode: AxisMode,
  grid: number,
): Vec3 {
  const axes: Vec3[] =
    mode === 'vertical'
      ? [v3(0, 1, 0)]
      : mode === 'horizontal'
        ? [v3(1, 0, 0), v3(0, 0, 1)]
        : [v3(1, 0, 0), v3(0, 1, 0), v3(0, 0, 1)];

  let bestAxis: Vec3 | null = null;
  let bestT = 0;
  let bestScore = Infinity;
  for (const a of axes) {
    const hit = rayAxisPoint(o, d, last, a);
    if (!hit) continue;
    const sp = cam.projectPoint(hit, viewportW, viewportH);
    if (!sp) continue;
    const score = Math.hypot(sp.x - px, sp.y - py);
    if (score < bestScore) {
      bestScore = score;
      bestAxis = a;
      bestT = vDot(vSub(hit, last), a);
    }
  }
  if (!bestAxis) return last;
  const t = grid > 0 ? Math.round(bestT / grid) * grid : Math.round(bestT * 10) / 10;
  return vAdd(last, vMul(bestAxis, t));
}

/**
 * A point for a DIAGONAL leg: the ray's crossing of the plane through `last`
 * that squarely faces the camera. Two degrees of freedom, both of them under
 * the cursor, which is the only arrangement that feels like drawing.
 */
export function freePoint(last: Vec3, o: Vec3, d: Vec3, cam: Camera, grid: number): Vec3 {
  const n = cam.forward();
  const hit = rayPlanePoint(o, d, last, n) ?? rayPlanePoint(o, d, last, vMul(n, -1));
  if (!hit) return last;
  if (grid <= 0) return v3(Math.round(hit.x * 10) / 10, Math.round(hit.y * 10) / 10, Math.round(hit.z * 10) / 10);
  return v3(
    Math.round(hit.x / grid) * grid,
    Math.round(hit.y / grid) * grid,
    Math.round(hit.z / grid) * grid,
  );
}
